/* ============================================================
   AL-HURUF PREMIUM PAGE TRANSITION ENGINE  v10 — "Gallery Reveal"
   Calligraphy emerges from darkness under soft gold light,
   then dissolves and merges into the destination page.

   Design rules this build honors:
     - No bounce / elastic easing anywhere. Every tween is a
       monotonic ease (cubic / quart) — value moves toward its
       target and stops. Nothing overshoots and settles back.
     - No spinning. No element rotates continuously or loops.
       (v9's rotating rings, rotating ray burst and rotating
       sacred-geometry canvas have all been removed.)
     - No sudden movement. Every fade/scale runs 500ms+ and uses
       an ease-out or ease-in-out curve, never a hard cut.
     - Only opacity, transform (translate/scale) and — sparingly,
       for the "emerging from darkness" softness — filter: blur/
       brightness are animated. Nothing that forces layout
       (width/height/top/left) is ever tweened.
     - The content swap only happens once the overlay has had
       time to reach full opacity, so the new page can never
       flash through mid-transition.
     - Exit is a true dissolve: the calligraphy fades outward
       into soft light at the same moment the overlay itself
       fades, so the artwork visually "merges" into the page
       instead of the overlay simply disappearing afterwards.

   API (unchanged): window.calligTransition.play(renderCb, doneCb)
                     window.calligTransition.stop(silent)
   ============================================================ */
(function () {
  'use strict';

  /* ---- monotonic easings only (no overshoot / no bounce) ---- */
  function eOutCubic(t){ return 1-Math.pow(1-t,3); }
  function eOutQuart(t){ return 1-Math.pow(1-t,4); }
  function eInOutCubic(t){ return t<0.5 ? 4*t*t*t : 1-Math.pow(-2*t+2,3)/2; }
  function clamp(v,lo,hi){ return Math.max(lo,Math.min(hi,v)); }

  function tween(dur,fn,step){
    return new Promise(function(resolve){
      var t0=performance.now();
      function tick(now){
        var p=Math.min((now-t0)/dur,1);
        step(fn(p),p);
        if(p<1) requestAnimationFrame(tick); else resolve();
      }
      requestAnimationFrame(tick);
    });
  }
  function wait(ms){ return new Promise(function(r){setTimeout(r,ms);}); }

  var canvas,ctx,vignette,glowOuter,glow,rays;
  var ringOuter,ringOuterC,ringInner,ringInnerC;
  var logo,shimmer,tagline;
  var OUTER_R=244, OUTER_CIRC=2*Math.PI*244;
  var INNER_R=154, INNER_CIRC=2*Math.PI*154;
  var _resizeBound=false;

  function initEls(){
    canvas    =document.getElementById('pt-canvas');
    ctx       =canvas?canvas.getContext('2d'):null;
    vignette  =document.getElementById('pt-vignette');
    glowOuter =document.getElementById('pt-glow-outer');
    glow      =document.getElementById('pt-glow');
    rays      =document.getElementById('pt-rays');
    ringOuter =document.getElementById('pt-ring-outer');
    ringOuterC=document.getElementById('pt-ring-outer-circle');
    ringInner =document.getElementById('pt-ring-inner');
    ringInnerC=document.getElementById('pt-ring-inner-circle');
    logo      =document.getElementById('pt-logo');
    shimmer   =document.getElementById('pt-shimmer');
    tagline   =document.getElementById('pt-tagline');
    if(ringOuterC) ringOuterC.style.strokeDasharray=OUTER_CIRC;
    if(ringInnerC) ringInnerC.style.strokeDasharray=INNER_CIRC;
    if(canvas&&!_resizeBound){
      _resizeBound=true;
      resizeCanvas();
      window.addEventListener('resize',resizeCanvas,{passive:true});
    }
  }

  function resizeCanvas(){
    if(!canvas) return;
    canvas.width=window.innerWidth;
    canvas.height=window.innerHeight;
  }

  /* Static sacred-geometry motif — fades only, never rotates. */
  var geoAlpha=0;
  function drawGeometry(){
    if(!ctx||geoAlpha<=0) return;
    var W=canvas.width,H=canvas.height;
    var cx=W/2,cy=H/2,r=Math.min(W,H)*0.42,N=6;
    ctx.save();
    ctx.translate(cx,cy);
    ctx.strokeStyle='#C9A96E';
    ctx.lineWidth=0.5;
    [0.28,0.52,0.78].forEach(function(f,fi){
      ctx.beginPath();
      for(var i=0;i<=N;i++){
        var a=(i/N)*Math.PI*2-Math.PI/6;
        i===0?ctx.moveTo(Math.cos(a)*r*f,Math.sin(a)*r*f):ctx.lineTo(Math.cos(a)*r*f,Math.sin(a)*r*f);
      }
      ctx.closePath();
      ctx.globalAlpha=geoAlpha*(fi===1?0.70:0.40);
      ctx.stroke();
    });
    ctx.globalAlpha=geoAlpha*0.25;
    for(var i=0;i<N;i++){
      var a2=(i/N)*Math.PI*2-Math.PI/6;
      ctx.beginPath();ctx.moveTo(0,0);
      ctx.lineTo(Math.cos(a2)*r*0.82,Math.sin(a2)*r*0.82);ctx.stroke();
    }
    ctx.globalAlpha=geoAlpha*0.18;ctx.beginPath();
    for(var j=0;j<N;j++){
      var a3=(j/N)*Math.PI*2-Math.PI/6,a4=((j+2)/N)*Math.PI*2-Math.PI/6;
      ctx.moveTo(Math.cos(a3)*r*0.78,Math.sin(a3)*r*0.78);
      ctx.lineTo(Math.cos(a4)*r*0.78,Math.sin(a4)*r*0.78);
    }
    ctx.stroke();
    ctx.restore();
  }

  /* Gold dust — gentle outward drift only, no gravity / no bounce. */
  var particles=[],pActive=false,pAlphaMul=1;
  function spawnParticles(){
    var cx=canvas.width/2,cy=canvas.height/2;
    particles=[];
    for(var i=0;i<16;i++){
      var a=(i/16)*Math.PI*2+(Math.random()-0.5)*0.4;
      particles.push({x:cx+Math.cos(a)*30,y:cy+Math.sin(a)*30,
        vx:Math.cos(a)*(0.35+Math.random()*0.55),vy:Math.sin(a)*(0.35+Math.random()*0.55),
        size:1+Math.random()*2,maxDist:170+Math.random()*220,traveled:0,
        hue:40+Math.random()*12,light:58+Math.random()*20});
    }
    for(var k=0;k<14;k++){
      var b=Math.random()*Math.PI*2;
      particles.push({x:cx+Math.cos(b)*(20+Math.random()*60),y:cy+Math.sin(b)*(20+Math.random()*60),
        vx:Math.cos(b)*(0.12+Math.random()*0.22),vy:Math.sin(b)*(0.12+Math.random()*0.22),
        size:0.5+Math.random()*1.1,maxDist:260+Math.random()*300,traveled:0,
        hue:44+Math.random()*10,light:62+Math.random()*18});
    }
    pActive=true;
  }

  function tickParticles(){
    if(!pActive||!ctx) return;
    var alive=0;
    for(var i=0;i<particles.length;i++){
      var p=particles[i];
      p.vx*=0.996;p.vy*=0.996;p.x+=p.vx;p.y+=p.vy;
      p.traveled+=Math.sqrt(p.vx*p.vx+p.vy*p.vy);
      var life=clamp(1-p.traveled/p.maxDist,0,1)*pAlphaMul;
      if(life<=0.002) continue; alive++;
      ctx.beginPath();ctx.arc(p.x,p.y,p.size*life,0,6.283185);
      ctx.fillStyle='hsla('+p.hue+',60%,'+p.light+'%,'+(life*0.72).toFixed(3)+')';ctx.fill();
      if(p.size>1.3&&life>0.25){
        ctx.beginPath();ctx.arc(p.x,p.y,p.size*3.2*life,0,6.283185);
        ctx.fillStyle='hsla('+p.hue+',60%,'+p.light+'%,'+(life*0.08).toFixed(3)+')';ctx.fill();
      }
    }
    if(alive===0) pActive=false;
  }

  var looping=false,mainRafId=null;
  function startLoop(){
    if(looping) return; looping=true;
    (function loop(){
      if(!ctx) return;
      ctx.clearRect(0,0,canvas.width,canvas.height);
      drawGeometry();tickParticles();
      if(looping) mainRafId=requestAnimationFrame(loop);
      else if(ctx) ctx.clearRect(0,0,canvas.width,canvas.height);
    })();
  }
  function stopLoop(){
    looping=false;
    if(mainRafId){cancelAnimationFrame(mainRafId);mainRafId=null;}
    if(ctx&&canvas) ctx.clearRect(0,0,canvas.width,canvas.height);
    particles=[];pActive=false;geoAlpha=0;pAlphaMul=1;
  }

  /* Resting state — everything hidden, no scale-from-zero "pop",
     just gently smaller / softer than its resolved state. */
  function reset(){
    stopLoop();
    if(logo) logo.style.cssText='opacity:0;transform:scale(.92) translateZ(0);filter:blur(20px) brightness(1.8);will-change:opacity,transform,filter;';
    if(glow) glow.style.cssText='opacity:0;transform:scale(.55) translateZ(0);will-change:opacity,transform;';
    if(glowOuter) glowOuter.style.cssText='opacity:0;transform:scale(.55) translateZ(0);will-change:opacity,transform;';
    if(rays){rays.style.cssText='opacity:0;will-change:opacity;';}
    if(ringOuter){ringOuter.style.cssText='opacity:0;will-change:opacity;';}
    if(ringInner){ringInner.style.cssText='opacity:0;will-change:opacity;';}
    if(ringOuterC) ringOuterC.style.strokeDashoffset=OUTER_CIRC;
    if(ringInnerC) ringInnerC.style.strokeDashoffset=INNER_CIRC;
    if(shimmer) shimmer.classList.remove('pt-shimmer-active');
    if(vignette){vignette.style.cssText='opacity:0;will-change:opacity;';}
    if(tagline){tagline.style.cssText='opacity:0;transform:translateY(8px) translateZ(0);will-change:opacity,transform;';}
  }

  var _renderCb=null,_doneCb=null;

  window.calligTransition = {
    play: function(renderCb,doneCb){
      initEls(); reset();
      _renderCb=renderCb; _doneCb=doneCb;

      (async function(){
        startLoop();

        /* ---- content swap: only once the overlay is fully opaque
           (CSS .is-active fade is 500ms) so nothing ever flashes. */
        await wait(520);
        if(_renderCb){var rc=_renderCb;_renderCb=null;rc();}

        /* ---- EMERGE: calligraphy rises softly out of darkness ---- */
        var pBloom = tween(900,eOutCubic,function(v){
          if(glowOuter){glowOuter.style.opacity=(v*0.55).toFixed(4);glowOuter.style.transform='scale('+(0.55+0.45*v).toFixed(4)+') translateZ(0)';}
        });
        var pRays = tween(850,eOutCubic,function(v){
          if(rays) rays.style.opacity=(v*0.5).toFixed(4);
        });
        var pGeo = tween(900,eOutCubic,function(v){ geoAlpha=v*0.045; });

        var pRingOuter = tween(700,eOutCubic,function(v,p){
          if(ringOuterC) ringOuterC.style.strokeDashoffset=(OUTER_CIRC*(1-v)).toFixed(2);
          if(ringOuter) ringOuter.style.opacity=clamp(p*3,0,0.42).toFixed(4);
        });
        await wait(220);
        var pRingInner = tween(550,eOutCubic,function(v,p){
          if(ringInnerC) ringInnerC.style.strokeDashoffset=(INNER_CIRC*(1-v)).toFixed(2);
          if(ringInner) ringInner.style.opacity=clamp(p*3,0,0.52).toFixed(4);
        });

        var pLogo = tween(1100,eOutQuart,function(v){
          if(!logo) return;
          var scale=0.92+0.08*v;
          logo.style.opacity=v.toFixed(4);
          logo.style.transform='scale('+scale.toFixed(4)+') translateZ(0)';
          logo.style.filter='blur('+(20*(1-v)).toFixed(2)+'px) brightness('+(1.8-0.8*v).toFixed(3)+')';
        });
        var pGlow = tween(1000,eOutCubic,function(v){
          if(glow){glow.style.opacity=(v*0.92).toFixed(4);glow.style.transform='scale('+(0.55+0.45*v).toFixed(4)+') translateZ(0)';}
        });

        tween(600,eOutCubic,function(v){
          if(vignette) vignette.style.opacity=(v*0.30).toFixed(4);
        });
        tween(650,eOutCubic,function(v){
          if(tagline){tagline.style.opacity=(v*0.70).toFixed(4);tagline.style.transform='translateY('+(8*(1-v)).toFixed(2)+'px) translateZ(0)';}
        });

        await pLogo; await pGlow; await pRingOuter; await pRingInner; await pBloom; await pRays; await pGeo;

        if(shimmer) shimmer.classList.add('pt-shimmer-active');
        spawnParticles();

        /* ---- HOLD: a still, museum-like beat to let it register ---- */
        await wait(260);

        /* ---- DISSOLVE / MERGE: fire doneCb now so the overlay itself
           starts fading (700ms, see CSS) in the same breath as the
           artwork dissolving — they resolve into the page together. */
        if(_doneCb){var dc=_doneCb;_doneCb=null;dc();}

        var exitDur=700;
        var eLogo = tween(exitDur,eInOutCubic,function(v){
          if(!logo) return;
          logo.style.opacity=(1-v).toFixed(4);
          logo.style.transform='scale('+(1+0.045*v).toFixed(4)+') translateZ(0)';
          logo.style.filter='blur('+(14*v).toFixed(2)+'px) brightness('+(1+0.35*v).toFixed(3)+')';
        });
        var eGlow = tween(exitDur,eInOutCubic,function(v){
          if(glow){glow.style.opacity=(0.92*(1-v)).toFixed(4);glow.style.transform='scale('+(1+0.12*v).toFixed(4)+') translateZ(0)';}
          if(glowOuter){glowOuter.style.opacity=(0.55*(1-v)).toFixed(4);glowOuter.style.transform='scale('+(1+0.16*v).toFixed(4)+') translateZ(0)';}
        });
        var eRingOuter = tween(620,eInOutCubic,function(v){
          if(ringOuterC) ringOuterC.style.strokeDashoffset=(OUTER_CIRC*v).toFixed(2);
          if(ringOuter) ringOuter.style.opacity=(0.42*(1-v)).toFixed(4);
        });
        var eRingInner = tween(560,eInOutCubic,function(v){
          if(ringInnerC) ringInnerC.style.strokeDashoffset=(INNER_CIRC*v).toFixed(2);
          if(ringInner) ringInner.style.opacity=(0.52*(1-v)).toFixed(4);
        });
        var eRays = tween(600,eInOutCubic,function(v){ if(rays) rays.style.opacity=(0.5*(1-v)).toFixed(4); });
        var eGeo  = tween(600,eInOutCubic,function(v){ geoAlpha=0.045*(1-v); });
        var eVig  = tween(550,eInOutCubic,function(v){ if(vignette) vignette.style.opacity=(0.30*(1-v)).toFixed(4); });
        tween(420,eOutCubic,function(v){
          if(tagline){tagline.style.opacity=(0.70*(1-v)).toFixed(4);tagline.style.transform='translateY('+(-6*v).toFixed(2)+'px) translateZ(0)';}
        });
        tween(500,eOutCubic,function(v){ pAlphaMul=1-v; });

        await eLogo; await eGlow; await eRingOuter; await eRingInner; await eRays; await eGeo; await eVig;
        stopLoop();
      })().catch(function(){
        if(_renderCb){var rc=_renderCb;_renderCb=null;rc();}
        if(_doneCb){var dc=_doneCb;_doneCb=null;dc();}
        stopLoop();
      });
    },

    stop: function(silent){
      stopLoop();
      if(logo) logo.style.cssText='';
      if(glow) glow.style.cssText='';
      if(glowOuter) glowOuter.style.cssText='';
      if(rays) rays.style.cssText='';
      if(ringOuter) ringOuter.style.cssText='';
      if(ringInner) ringInner.style.cssText='';
      if(vignette) vignette.style.cssText='';
      if(tagline) tagline.style.cssText='';
      if(shimmer) shimmer.classList.remove('pt-shimmer-active');
      if(!silent){
        if(_renderCb){var rc=_renderCb;_renderCb=null;rc();}
        if(_doneCb){var dc=_doneCb;_doneCb=null;dc();}
      } else { _renderCb=null;_doneCb=null; }
    }
  };
}());
